import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';

import { TableCell } from './TableCell';
import { tablePropTypes } from './PropTypes';
import { Tooltip } from '../Tooltip';
import { isEqual as _isEqual } from '../../utils/compare';
import { getViewportWidth } from '../../utils/window';

import styles from './TableRow.module.scss';

export class TableRow extends Component {
  constructor(props) {
    super(props);
    this.tooltipTimeout = null;
    this.tooltipUnmountTimeout = null;
    this.rowRef = createRef();
    this.tooltipRef = createRef();
    this.state = {
      showTooltip: false,
      tooltipX: 0,
      tooltipY: 0,
      width: 0,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    let isEqualRowIndex = true;

    if (this.props.rowIndex === this.props.hoveredRowIndex) {
      if (this.props.rowIndex !== nextProps.hoveredRowIndex) {
        isEqualRowIndex = false;
      }
    } else if (this.props.rowIndex === nextProps.hoveredRowIndex) {
      isEqualRowIndex = false;
    }

    return !(
      _isEqual(
        [
          'rowIndex',
          'freezeColumns',
          'onContextMenu',
          'rowClickHandler',
          'isAllSelected',
          'isSomeSelected',
          'isSkeleton',
        ],
        this.props,
        nextProps,
      ) &&
      isEqual(nextProps.data, this.props.data) &&
      isEqual(nextProps.body, this.props.body) &&
      isEqualRowIndex &&
      this.state.showTooltip === nextState.showTooltip &&
      this.state.tooltipX === nextState.tooltipX &&
      this.state.tooltipY === nextState.tooltipY
    );
  }

  updateTooltipPosition = () => {
    const tooltipContents = this.tooltipRef?.current?.querySelector?.('.tooltip__label');

    if (tooltipContents) {
      const boundingRect = tooltipContents.getBoundingClientRect?.();

      if (boundingRect && tooltipContents) {
        const viewportWidth = getViewportWidth();

        if (boundingRect.right + 8 > viewportWidth) {
          this.setState({ tooltipX: viewportWidth - 8 - boundingRect.width / 2 });
        } else if (boundingRect.left <= 8) {
          this.setState({ tooltipX: 8 + boundingRect.width / 2 });
        }
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.showTooltip !== this.state.showTooltip && this.state.showTooltip) {
      this.updateTooltipPosition();
    }
  }

  clearTooltipTimeout = () => {
    if (this.tooltipTimeout) {
      window.clearTimeout(this.tooltipTimeout);
    }
  };

  componentWillUnmount() {
    this.clearTooltipTimeout();
  }

  handleTrMouseOver = (e) => {
    const { getRowTooltip, rowTooltipDelay, isSkeleton } = this.props;
    const { showTooltip } = this.state;
    const { y } = this.rowRef.current?.getBoundingClientRect?.() || { y: e.clientY + 20 || 0 };
    const x = e.clientX;

    if (
      getRowTooltip &&
      !isSkeleton &&
      !showTooltip &&
      this.rowRef.current?.getBoundingClientRect
    ) {
      this.clearTooltipTimeout();

      let timeoutTime = rowTooltipDelay || 1000;
      if (timeoutTime <= 200) {
        timeoutTime = 201;
      }

      this.tooltipTimeout = window.setTimeout(() => {
        this.setState({ tooltipX: x, tooltipY: y, showTooltip: true });
      }, timeoutTime);
    }
  };

  handleTrMouseLeave = (e) => {
    const { trOnMouseLeave } = this.props;

    this.clearTooltipTimeout();
    this.setState({ showTooltip: false });

    if (trOnMouseLeave) {
      trOnMouseLeave(e);
    }
  };

  render() {
    const {
      rowIndex,
      data,
      body,
      freezeColumns = 0,
      onContextMenu,
      rowClickHandler,
      hoveredRowIndex,
      trOnMouseEnter,
      tdOnMouseOver,
      tdOnMouseLeave,
      isSkeleton,
      tbodyRowHeight,
      isAllSelected,
      isSomeSelected,
      getRowTooltip,
    } = this.props;

    const { showTooltip, tooltipX, tooltipY } = this.state;

    let trProps;
    if (!isSkeleton) {
      trProps = {
        onClick: (e) => rowClickHandler(e, data, rowIndex),
        className: `${hoveredRowIndex === rowIndex ? 'tr-hover' : ''} ${data.rowClassName || ''}`,
        onMouseEnter: trOnMouseEnter,
        onMouseLeave: this.handleTrMouseLeave,
        onMouseOver: this.handleTrMouseOver,
      };
    } else {
      trProps = { className: `loader` };
    }

    return (
      <>
        {!isSkeleton &&
          !!getRowTooltip &&
          ReactDOM.createPortal(
            <div
              className={styles.tableRowTooltip}
              style={{ left: tooltipX, top: tooltipY }}
              ref={this.tooltipRef}
            >
              <Tooltip
                label={getRowTooltip(data, rowIndex)}
                positon="top"
                auto={false}
                portal={false}
                getContentOnly
                contentOnlyProps={{
                  isVisible: showTooltip,
                }}
                onSetLabelMaxWidth={() => this.updateTooltipPosition()}
              />
            </div>,
            document.body,
          )}
        <tr ref={this.rowRef} {...trProps}>
          {body.map((field, colIndex) => {
            let tdProps = {
              field: field,
              data: data,
              tbodyRowHeight: tbodyRowHeight,
            };
            if (!isSkeleton) {
              tdProps = {
                ...tdProps,
                colIndex: colIndex,
                onContextMenu: onContextMenu,
                rowIndex: rowIndex,
                freezeColumns: freezeColumns,
                tdOnMouseOver: tdOnMouseOver,
                tdOnMouseLeave: tdOnMouseLeave,
                isAllSelected: isAllSelected,
                isSomeSelected: isSomeSelected,
              };
            } else {
              tdProps = {
                ...tdProps,
                isSkeleton: true,
              };
            }

            return <TableCell {...tdProps} key={colIndex} />;
          })}
        </tr>
      </>
    );
  }
}

TableRow.propTypes = {
  body: tablePropTypes.body,
  freeze: tablePropTypes.freeze,
  rowIndex: PropTypes.number,
  data: PropTypes.any,
  onContextMenu: PropTypes.func,
  rowClickHandler: PropTypes.func,
  freezeColumns: PropTypes.number,
  hoveredRowIndex: PropTypes.number,
  trOnMouseEnter: PropTypes.func,
  trOnMouseLeave: PropTypes.func,
  tdOnMouseOver: PropTypes.func,
  tdOnMouseLeave: PropTypes.func,
  isSkeleton: PropTypes.bool,
  tbodyRowHeight: PropTypes.number,
  getRowTooltip: PropTypes.func,
  rowTooltipDelay: PropTypes.number,
};
